import { ActionIcon, Button, FileButton, Skeleton, Tooltip } from '@mantine/core';
import {
  IconAlertTriangleFilled,
  IconCalendarFilled,
  IconClockFilled,
  IconCloudUpload,
  IconDeviceFloppy,
  IconEye,
  IconFileText,
  IconInfoCircleFilled,
  IconLockOpen,
  IconPaperclip,
  IconPrinter,
  IconSend,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { AttachmentCard } from '../pms/AttachmentCard.js';
import type { PmsAttachment } from '../pms/types.js';
import { DOCUMENT_ACCEPT } from '../port-calls/PortCallParts.js';
import classes from './NoonReportScreen.module.css';
import { formatStamp } from './NoonSections.js';
import sections from './ReportSections.module.css';

/**
 * Sefer ekranlarının sağ sütun kartları (sefer-noon-report.jpeg; M6f G4-25, G4-56, G4-60): rapor
 * zamanı, uyarılar, ekler, kaydet / gönder ve son gönderim. Noon Report ve Leg / Event aynı
 * kartları kullanır.
 */

/** Yan kart başlığı: ikon ve başlık, altında çizgi (G4-25). */
export function SideTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className={classes.sideTitle}>
      <span className={classes.sideIcon} aria-hidden>
        {icon}
      </span>
      <h2>{title}</h2>
    </div>
  );
}

/** Yan sütunun üstündeki rapor zamanı kartı: etiket, tarih tek satırda, durum rozeti (G4-56). */
export function ReportTimeCard({
  label,
  ariaLabel,
  at,
  status,
  loading = false,
}: {
  label: string;
  /** Bölge adı; görünen etiketten farklıysa (Leg / Event: olay türü görünür, ad "Event Time"). */
  ariaLabel?: string;
  at: string | null;
  status: { tone: StatusTone; text: string; icon?: ReactNode } | null;
  /** Yüklenirken sahte zaman gösterilmez (G4-07). */
  loading?: boolean;
}) {
  return (
    <section className={classes.timeCard} aria-label={ariaLabel ?? label}>
      <span className={classes.timeIcon} aria-hidden>
        <IconCalendarFilled size={24} />
      </span>
      <span className={classes.timeLabel}>{label}</span>
      {status && !loading ? (
        <span className={classes.timeBadge}>
          <StatusBadge tone={status.tone} withIcon={!status.icon} icon={status.icon}>
            {status.text}
          </StatusBadge>
        </span>
      ) : null}
      {loading ? (
        <Skeleton height={20} width={150} className={classes.timeValue} />
      ) : (
        <strong className={classes.timeValue}>
          {formatStamp(at)} <small>(UTC)</small>
        </strong>
      )}
    </section>
  );
}

export interface AlertLine {
  key: string;
  text: string;
  tone: 'info' | 'warning';
  /** Sağda zaman ya da kısa bilgi. */
  aside?: string;
}

/** Uyarılar ve bildirimler: satır ikonu 28 px tonlu çipte; uyarı yoksa tek satır (G4-25, G4-60). */
export function AlertsCard({
  title,
  lines,
  empty,
}: {
  title: string;
  lines: readonly AlertLine[];
  empty: string;
}) {
  const shown: AlertLine[] =
    lines.length === 0 ? [{ key: 'none', text: empty, tone: 'warning' }] : [...lines];
  return (
    <section className={classes.sideCard} aria-label={title}>
      <SideTitle
        icon={<IconAlertTriangleFilled size={22} className={classes.warnIcon} />}
        title={title}
      />
      <ul className={classes.alertList}>
        {shown.map((line) => (
          <li key={line.key} className={classes.alertLine}>
            <span className={classes.alertChip} data-tone={line.tone} aria-hidden>
              {line.tone === 'info' ? (
                <IconInfoCircleFilled size={16} />
              ) : (
                <IconAlertTriangleFilled size={16} />
              )}
            </span>
            <span className={classes.alertText}>
              {line.text}
              {line.aside ? <small>{line.aside}</small> : null}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Ekler (ADR 0057 §3, §7): raporun PDF'i ("Print / PDF") ve destekleyici belgeler. Belge yalnız
 * kaydedilmiş taslağa ya da yeniden açılmış rapora gemide yüklenir; gönderilmiş raporun ekleri
 * salt okunur. Ofiste belge, buluta ulaşmışsa adresinden açılır.
 */
export function AttachmentsCard({
  pdfLabel,
  onPrint,
  printing = false,
  documents,
  hrefOf,
  onUpload,
  uploadHint,
  uploading = false,
}: {
  /** "Noon Report PDF" gibi. */
  pdfLabel: string;
  /** PDF'i üretir (yazdırma ya da kaydetme); rapor yoksa verilmez. */
  onPrint?: (() => void) | undefined;
  printing?: boolean;
  /** `null`: yükleniyor ya da rapor henüz kaydedilmedi. */
  documents: readonly PmsAttachment[] | null;
  hrefOf?: ((id: string) => string) | undefined;
  onUpload?: ((file: File) => void) | undefined;
  /** Yükleme yapılamıyorsa nedeni (ör. "önce taslağı kaydedin"). */
  uploadHint?: string | null | undefined;
  uploading?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.noon.attachments.${key}`, options ?? {});
  const live = (documents ?? []).filter((d) => !d.voided);
  return (
    <section className={classes.sideCard} aria-label={k('title')}>
      <SideTitle icon={<IconPaperclip size={22} stroke={2} />} title={k('title')} />
      <ul className={classes.attachmentList}>
        <li className={classes.attachment}>
          <IconFileText size={20} aria-hidden />
          <span>
            <strong>{pdfLabel}</strong>
            <small>{onPrint ? k('pdfReady') : k('notGenerated')}</small>
          </span>
          {onPrint ? (
            <Tooltip label={k('print')} withArrow>
              <ActionIcon
                variant="default"
                size={34}
                loading={printing}
                aria-label={k('print')}
                onClick={onPrint}
              >
                <IconPrinter size={18} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </li>
        <li className={classes.attachment}>
          <IconPaperclip size={20} aria-hidden />
          <span>
            <strong>{k('supporting')}</strong>
            <small>{k('files', { count: live.length })}</small>
          </span>
          {onUpload ? (
            <FileButton onChange={(file) => file && onUpload(file)} accept={DOCUMENT_ACCEPT}>
              {(props) => (
                <Tooltip label={k('uploadTypes')} withArrow>
                  <ActionIcon
                    {...props}
                    variant="default"
                    size={34}
                    loading={uploading}
                    aria-label={k('upload')}
                  >
                    <IconCloudUpload size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </FileButton>
          ) : uploadHint ? (
            <Tooltip label={uploadHint} withArrow>
              <ActionIcon variant="default" size={34} disabled aria-label={k('upload')}>
                <IconCloudUpload size={18} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </li>
      </ul>
      {documents && documents.length > 0 ? (
        <ul className={sections.attachments} aria-label={k('attached', { count: live.length })}>
          {documents.map((document) => (
            <AttachmentCard
              key={document.id}
              attachment={document}
              {...(hrefOf ? { href: hrefOf(document.id) } : {})}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** Son gönderim: saat ikonu renkli dairede (G4-25, G4-62). */
export function LastSubmitted({ at, by }: { at: string | null; by: string | null }) {
  const { t } = useTranslation();
  if (!at) return null;
  return (
    <p className={classes.last}>
      <span className={classes.lastIcon} aria-hidden>
        <IconClockFilled size={14} />
      </span>
      <span>
        {t('voyage.noon.lastSubmitted', { at: formatStamp(at) })}
        {by ? (
          <>
            <br />
            {t('voyage.noon.by', { name: by })}
          </>
        ) : null}
      </span>
    </p>
  );
}

export type ActionMessage = { tone: 'success' | 'danger'; text: string } | null;

/**
 * Kaydet, gönder ve yeniden aç. Yan sütunda alt alta (yapışkan beyaz kart); Noon Report
 * Machinery'de ana sütunun altında çubuk: solda Save Draft, Print, Preview; sağda Submit (G4-29).
 * İletiler `.notice` kutusunda (G4-62).
 */
export function ReportActions({
  readOnly,
  busy,
  canReopen,
  canSubmit,
  onSave,
  onSubmit,
  onReopen,
  layout,
  message,
  submitLabel,
  extra,
  onPrint,
  onPreview,
  printing = null,
  lockedNote = null,
}: {
  readOnly: boolean;
  busy: string | null;
  canReopen: boolean;
  canSubmit: boolean;
  onSave: () => void;
  onSubmit: () => void;
  onReopen: () => void;
  layout: 'stack' | 'bar';
  message: ActionMessage;
  submitLabel?: string;
  /**
   * Ek düğmeler (taslağı sil, gerekçeli iptal; ADR 0077): yığının altında, çubukta iletinin
   * önünde; kilitli raporda da gösterilir.
   */
  extra?: ReactNode;
  /** Kilitli raporda kişinin düzeltememesinin nedeni (ör. yalnız kaptan yeniden açar). */
  lockedNote?: string | null;
  /** Ortak PDF (ADR 0057 §7): yazdırma penceresiyle açar. */
  onPrint?: (() => void) | undefined;
  /** PDF'i yazdırmadan açar ya da kaydeder. */
  onPreview?: (() => void) | undefined;
  printing?: 'print' | 'preview' | null;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.noon.actions.${key}`);
  const note = message ? (
    <p
      className={[classes.notice, classes.message].join(' ')}
      data-tone={message.tone}
      role={message.tone === 'danger' ? 'alert' : 'status'}
    >
      {message.tone === 'danger' ? (
        <IconAlertTriangleFilled size={16} aria-hidden />
      ) : (
        <IconInfoCircleFilled size={16} aria-hidden />
      )}
      <span>{message.text}</span>
    </p>
  ) : null;
  const printButton = onPrint ? (
    <Button
      size="md"
      variant={layout === 'bar' ? 'outline' : 'default'}
      fullWidth={layout === 'stack'}
      leftSection={<IconPrinter size={18} />}
      loading={printing === 'print'}
      disabled={printing !== null}
      onClick={onPrint}
    >
      {k('printPdf')}
    </Button>
  ) : null;
  if (readOnly) {
    return (
      <div className={classes.actions} data-layout={layout}>
        {printButton}
        {canReopen ? (
          <Button
            size="md"
            variant="default"
            fullWidth={layout === 'stack'}
            leftSection={<IconLockOpen size={18} />}
            onClick={onReopen}
          >
            {k('reopen')}
          </Button>
        ) : null}
        {extra}
        {lockedNote ? (
          <p className={classes.notice} data-tone="info">
            <IconInfoCircleFilled size={16} aria-hidden />
            <span>{lockedNote}</span>
          </p>
        ) : null}
        {note}
      </div>
    );
  }
  const saveButton = (
    <Button
      size="md"
      fullWidth={layout === 'stack'}
      variant={layout === 'bar' ? 'outline' : 'filled'}
      leftSection={<IconDeviceFloppy size={18} />}
      loading={busy === 'save'}
      disabled={busy !== null}
      onClick={onSave}
    >
      {k('saveDraft')}
    </Button>
  );
  const submitButton = (
    <Button
      size="md"
      fullWidth={layout === 'stack'}
      className={layout === 'stack' ? classes.submit : undefined}
      leftSection={<IconSend size={18} />}
      loading={busy === 'submit'}
      disabled={busy !== null || !canSubmit}
      onClick={onSubmit}
    >
      {submitLabel ?? k('submit')}
    </Button>
  );
  if (layout === 'bar') {
    return (
      <div className={classes.actions} data-layout="bar">
        <div className={classes.actionGroup}>
          {saveButton}
          {printButton ?? (
            <Tooltip label={k('printLater')} withArrow>
              <Button size="md" variant="outline" leftSection={<IconPrinter size={18} />} disabled>
                {k('print')}
              </Button>
            </Tooltip>
          )}
          {onPreview ? (
            <Button
              size="md"
              variant="outline"
              leftSection={<IconEye size={18} />}
              loading={printing === 'preview'}
              disabled={printing !== null}
              onClick={onPreview}
            >
              {k('preview')}
            </Button>
          ) : (
            <Tooltip label={k('printLater')} withArrow>
              <Button size="md" variant="outline" leftSection={<IconEye size={18} />} disabled>
                {k('preview')}
              </Button>
            </Tooltip>
          )}
        </div>
        {extra}
        {note}
        {submitButton}
      </div>
    );
  }
  return (
    <div className={classes.actions} data-layout="stack">
      {saveButton}
      {submitButton}
      {printButton}
      {extra}
      {note}
    </div>
  );
}
